import type { HeadsUpIntroConfig, HeadsUpIntroTier } from 'featureFlags/headsUpIntroConfig';

/**
 * Player-facing copy for the one-time heads-up Sit & Go introduction (EN-10748). Jason
 * owns the copy; Ryan signs it; the spec is the TCH knowledge base,
 * `products/hijack-poker/specs/EN-10730-heads-up-sit-and-go/EN-10748-announcement-and-intro-modal.md`.
 *
 * Written for a reader who has never played a Sit & Go, let alone heads-up (Jason,
 * 2026-10-25): the format is named at the top, both terms are defined in a line each,
 * the steps follow the real journey (the Sit & Go tab, a buy-in section, a Heads-up
 * seat, the game) and the ladder shows what each buy-in plays for.
 *
 * Two kinds, split as the multi-run intro splits them (EN-10791):
 *   - TIMELESS copy lives here and renders for any field the flag's edition does not
 *     supply. The test: would every sentence still be true a year from now with no flag
 *     payload behind it? So no "live now on…", no prices.
 *   - TIMELY copy (the ladder, that it is live, Play Again) comes from the flag's
 *     edition. headsUpIntroEditions.ts holds the variations as written;
 *     featureFlags/headsUpIntroConfig.ts holds the rules.
 * Rules both kinds keep (the spec's, from the cold read of 2026-10-21):
 *   - The app's own names: Heads-up, Six-seat, Sit & Go, Play Again. Never "HU", "SNG".
 *   - A prize is always beside the buy-in that pays for it, never on its own.
 *   - No stack or level numbers, no "beta", no "no waiting", no em dashes.
 */
export const HEADS_UP_INTRO_TIMELESS = {
  // What the game is and what you play for, as two lines so a narrow screen wraps
  // inside one of them.
  titleLines: ['Just you and one rival.', 'Winner takes the pot.'],
  availability: ['Look for Heads-up on the Sit & Go tab.'],
  smallPrint: 'Until a game starts you can unregister and your buy-in comes straight back.'
} as const;

/** The parts that change only with the feature itself. */
export const HEADS_UP_INTRO_COPY = {
  /** The tag and the format's name, above the headline. */
  newTag: 'New',
  eyebrow: 'Heads-up Sit & Go',
  /** The two terms a new player has to learn, in a line each. */
  definitions: [
    { term: 'Sit & Go', text: 'A one-table tournament. It starts the moment every seat is taken.' },
    { term: 'Heads-up', text: 'Two seats: You and one opponent play for one pot.' }
  ],
  cardHeading: 'How it works',
  steps: {
    openTab: 'Open the Sit & Go tab.',
    pickBuyIn: 'Pick a buy-in. Each section on the tab is a price, and it sets the prize.',
    takeSeat: 'Take a Heads-up seat. The moment a second player sits down, the game starts.',
    play: 'Play until one of you has all the chips. The winner takes the whole pot.'
  },
  /** The ladder's column headings. */
  ladder: { buyIn: 'Buy-in', prize: 'Winner takes', caption: 'Buy-ins and what the winner takes' },
  /** The footer tab's own label, drawn as the tab is drawn. */
  tabLabel: 'Sit & Go',
  action: 'Find a Heads-up table',
  closeLabel: 'Close'
} as const;

/** What the modal renders: an edition's copy merged over the timeless copy. */
export type HeadsUpIntroContent = {
  /** The edition this is, or null for the timeless copy on its own (a preview). */
  edition: string | null;
  titleLines: readonly [string, string];
  /** The ladder; empty when the edition supplies none (the rows are not drawn). */
  tiers: readonly HeadsUpIntroTier[];
  availability: readonly string[];
  smallPrint: string;
};

export const resolveHeadsUpIntroContent = (
  config: HeadsUpIntroConfig | null
): HeadsUpIntroContent => ({
  edition: config?.edition ?? null,
  titleLines: config?.titleLines ?? HEADS_UP_INTRO_TIMELESS.titleLines,
  tiers: config?.tiers ?? [],
  availability: config?.availability ?? HEADS_UP_INTRO_TIMELESS.availability,
  smallPrint: config?.smallPrint ?? HEADS_UP_INTRO_TIMELESS.smallPrint
});

/** The dialog's accessible name: the format, then the headline as one sentence pair. */
export const headsUpIntroTitle = (content: Pick<HeadsUpIntroContent, 'titleLines'>): string =>
  `${HEADS_UP_INTRO_COPY.eyebrow} ${content.titleLines.join(' ')}`;
